function clean(value) {
    return String(value || "").trim();
}

function escapeSolidityString(value, fallback = "") {
    return clean(value || fallback)
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"');
}

function safeContractName(value, fallback = "CustomToken") {
    const raw = clean(value);

    const words = raw
        .replace(/[^A-Za-z0-9_ ]+/g, " ")
        .split(/[\s_]+/)
        .filter(Boolean);

    const name = words
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join("");

    const finalName = name || fallback;

    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(finalName)) {
        return finalName;
    }

    return fallback;
}

function parseModules(value) {
    if (Array.isArray(value)) {
        return value.map((item) => clean(item).toLowerCase()).filter(Boolean);
    }

    return clean(value)
        .split(",")
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
}

function hasModule(modules, name) {
    return parseModules(modules).includes(name);
}

function getSeedFromSearch(search, state) {
    const params = new URLSearchParams(search || "");
    const stateSeed = state?.customSeed || {};

    const tokenType = clean(params.get("type") || stateSeed.tokenType || "ERC20").toUpperCase();

    const safeType =
        tokenType === "ERC721" || tokenType === "ERC1155" || tokenType === "CUSTOM"
            ? tokenType
            : "ERC20";

    const tokenName =
        clean(params.get("name") || stateSeed.tokenName) ||
        (safeType === "ERC20"
            ? "My Custom Token"
            : safeType === "ERC721"
                ? "My Custom NFT"
                : "My Custom Multi Token");

    const tokenSymbol =
        clean(params.get("symbol") || stateSeed.tokenSymbol) ||
        (safeType === "ERC1155" ? "" : safeType === "ERC721" ? "MCNFT" : "MCT");

    const erc20Decimals = clean(
        params.get("decimals") || stateSeed.erc20Decimals || "18",
    );

    const erc20InitialSupply = clean(
        params.get("initialSupply") || stateSeed.erc20InitialSupply || "0",
    );

    const modules = parseModules(params.get("modules") || stateSeed.modules || "");
    const projectId =
        clean(params.get("projectId") || stateSeed.projectId) ||
        `custom_${safeType.toLowerCase()}_draft`;

    return {
        tokenType: safeType,
        tokenName,
        tokenSymbol,
        erc20Decimals,
        erc20InitialSupply,
        baseURI:
            clean(params.get("baseURI") || stateSeed.baseURI) ||
            (safeType === "ERC721"
                ? "ipfs://YOUR_METADATA_CID/"
                : safeType === "ERC1155"
                    ? "ipfs://YOUR_METADATA_CID/{id}.json"
                    : ""),
        royaltyFeeNumerator: clean(
            params.get("royaltyBps") || stateSeed.royaltyFeeNumerator || "0",
        ),
        royaltyReceiver: clean(
            params.get("royaltyReceiver") || stateSeed.royaltyReceiver || "",
        ),
        modules,
        selectedNetwork: clean(params.get("network") || stateSeed.selectedNetwork || ""),
        contractName: safeContractName(
            tokenName,
            safeType === "ERC20"
                ? "CustomToken"
                : safeType === "ERC721"
                    ? "CustomNFT"
                    : "CustomMultiToken",
        ),
        projectName: tokenName,
        projectId,
    };
}

function shouldUseRoyalty(seed) {
    if (seed.tokenType === "ERC20") return false;

    const bps = Number(clean(seed.royaltyFeeNumerator || "0"));
    return Number.isFinite(bps) && bps > 0;
}

function buildErc20Source(seed) {
    const contractName = safeContractName(seed.contractName, "CustomToken");
    const name = escapeSolidityString(seed.tokenName, "My Custom Token");
    const symbol = escapeSolidityString(seed.tokenSymbol, "MCT");

    const mintable = hasModule(seed.modules, "mintable");
    const burnable = hasModule(seed.modules, "burnable");
    const pausable = hasModule(seed.modules, "pausable");

    const imports = [
        'import "@openzeppelin/contracts/token/ERC20/ERC20.sol";',
        'import "@openzeppelin/contracts/access/AccessControl.sol";',
    ];

    if (burnable) {
        imports.push('import "@openzeppelin/contracts/token/ERC20/extensions/ERC20Burnable.sol";');
    }

    if (pausable) {
        imports.push('import "@openzeppelin/contracts/token/ERC20/extensions/ERC20Pausable.sol";');
    }

    const inherits = ["ERC20", "AccessControl"];

    if (burnable) inherits.push("ERC20Burnable");
    if (pausable) inherits.push("ERC20Pausable");

    return `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.24;

${imports.join("\n")}

contract ${contractName} is ${inherits.join(", ")} {
${mintable ? '    bytes32 public constant MINTER_ROLE = keccak256("MINTER_ROLE");\n' : ""}${pausable ? '    bytes32 public constant PAUSER_ROLE = keccak256("PAUSER_ROLE");\n' : ""}
    constructor(address admin_, uint256 initialSupply_) ERC20("${name}", "${symbol}") {
        require(admin_ != address(0), "admin required");

        _grantRole(DEFAULT_ADMIN_ROLE, admin_);
${mintable ? "        _grantRole(MINTER_ROLE, admin_);\n" : ""}${pausable ? "        _grantRole(PAUSER_ROLE, admin_);\n" : ""}
        if (initialSupply_ > 0) {
            _mint(admin_, initialSupply_);
        }
    }
${mintable ? `
    function mint(address to, uint256 amount) external onlyRole(MINTER_ROLE) {
        _mint(to, amount);
    }
` : ""}${pausable ? `
    function pause() external onlyRole(PAUSER_ROLE) {
        _pause();
    }

    function unpause() external onlyRole(PAUSER_ROLE) {
        _unpause();
    }

    function _update(address from, address to, uint256 value)
        internal
        override(ERC20, ERC20Pausable)
    {
        super._update(from, to, value);
    }
` : ""}
}
`;
}

function buildErc721Source(seed) {
    const contractName = safeContractName(seed.contractName, "CustomNFT");
    const name = escapeSolidityString(seed.tokenName, "My Custom NFT");
    const symbol = escapeSolidityString(seed.tokenSymbol, "MCNFT");

    const mintable = hasModule(seed.modules, "mintable");
    const burnable = hasModule(seed.modules, "burnable");
    const pausable = hasModule(seed.modules, "pausable");
    const royalty = shouldUseRoyalty(seed);

    const imports = [
        'import "@openzeppelin/contracts/token/ERC721/ERC721.sol";',
        'import "@openzeppelin/contracts/access/AccessControl.sol";',
    ];

    if (burnable) {
        imports.push('import "@openzeppelin/contracts/token/ERC721/extensions/ERC721Burnable.sol";');
    }

    if (pausable) {
        imports.push('import "@openzeppelin/contracts/token/ERC721/extensions/ERC721Pausable.sol";');
    }

    if (royalty) {
        imports.push('import "@openzeppelin/contracts/token/common/ERC2981.sol";');
    }

    const inherits = ["ERC721", "AccessControl"];

    if (burnable) inherits.push("ERC721Burnable");
    if (pausable) inherits.push("ERC721Pausable");
    if (royalty) inherits.push("ERC2981");

    const supportsOverride = royalty
        ? "override(ERC721, AccessControl, ERC2981)"
        : "override(ERC721, AccessControl)";

    return `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.24;

${imports.join("\n")}

contract ${contractName} is ${inherits.join(", ")} {
${mintable ? '    bytes32 public constant MINTER_ROLE = keccak256("MINTER_ROLE");\n' : ""}${pausable ? '    bytes32 public constant PAUSER_ROLE = keccak256("PAUSER_ROLE");\n' : ""}${royalty ? '    bytes32 public constant ROYALTY_MANAGER_ROLE = keccak256("ROYALTY_MANAGER_ROLE");\n' : ""}
    uint256 private _nextTokenId;
    string private _baseTokenURI;

    constructor(
        address admin_,
        string memory baseURI_${royalty ? ",\n        address royaltyReceiver_,\n        uint96 royaltyBps_" : ""}
    ) ERC721("${name}", "${symbol}") {
        require(admin_ != address(0), "admin required");

        _baseTokenURI = baseURI_;

        _grantRole(DEFAULT_ADMIN_ROLE, admin_);
${mintable ? "        _grantRole(MINTER_ROLE, admin_);\n" : ""}${pausable ? "        _grantRole(PAUSER_ROLE, admin_);\n" : ""}${royalty ? "        _grantRole(ROYALTY_MANAGER_ROLE, admin_);\n\n        if (royaltyReceiver_ != address(0) && royaltyBps_ > 0) {\n            _setDefaultRoyalty(royaltyReceiver_, royaltyBps_);\n        }\n" : ""}
    }

    function setBaseURI(string memory newBaseURI) external onlyRole(DEFAULT_ADMIN_ROLE) {
        _baseTokenURI = newBaseURI;
    }
${mintable ? `
    function safeMint(address to) external onlyRole(MINTER_ROLE) returns (uint256) {
        uint256 tokenId = _nextTokenId;
        _nextTokenId += 1;

        _safeMint(to, tokenId);

        return tokenId;
    }
` : ""}${pausable ? `
    function pause() external onlyRole(PAUSER_ROLE) {
        _pause();
    }

    function unpause() external onlyRole(PAUSER_ROLE) {
        _unpause();
    }

    function _update(address to, uint256 tokenId, address auth)
        internal
        override(ERC721, ERC721Pausable)
        returns (address)
    {
        return super._update(to, tokenId, auth);
    }
` : ""}${royalty ? `
    function setDefaultRoyalty(address receiver, uint96 feeNumerator)
        external
        onlyRole(ROYALTY_MANAGER_ROLE)
    {
        _setDefaultRoyalty(receiver, feeNumerator);
    }

    function deleteDefaultRoyalty() external onlyRole(ROYALTY_MANAGER_ROLE) {
        _deleteDefaultRoyalty();
    }
` : ""}
    function _baseURI() internal view override returns (string memory) {
        return _baseTokenURI;
    }

    function supportsInterface(bytes4 interfaceId)
        public
        view
        ${supportsOverride}
        returns (bool)
    {
        return super.supportsInterface(interfaceId);
    }
}
`;
}

function buildErc1155Source(seed) {
    const contractName = safeContractName(seed.contractName, "CustomMultiToken");

    const mintable = hasModule(seed.modules, "mintable");
    const burnable = hasModule(seed.modules, "burnable");
    const pausable = hasModule(seed.modules, "pausable");
    const royalty = shouldUseRoyalty(seed);

    const imports = [
        'import "@openzeppelin/contracts/token/ERC1155/ERC1155.sol";',
        'import "@openzeppelin/contracts/access/AccessControl.sol";',
    ];

    if (burnable) {
        imports.push('import "@openzeppelin/contracts/token/ERC1155/extensions/ERC1155Burnable.sol";');
    }

    if (pausable) {
        imports.push('import "@openzeppelin/contracts/token/ERC1155/extensions/ERC1155Pausable.sol";');
    }

    if (royalty) {
        imports.push('import "@openzeppelin/contracts/token/common/ERC2981.sol";');
    }

    const inherits = ["ERC1155", "AccessControl"];

    if (burnable) inherits.push("ERC1155Burnable");
    if (pausable) inherits.push("ERC1155Pausable");
    if (royalty) inherits.push("ERC2981");

    const supportsOverride = royalty
        ? "override(ERC1155, AccessControl, ERC2981)"
        : "override(ERC1155, AccessControl)";

    return `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.24;

${imports.join("\n")}

contract ${contractName} is ${inherits.join(", ")} {
${mintable ? '    bytes32 public constant MINTER_ROLE = keccak256("MINTER_ROLE");\n' : ""}${pausable ? '    bytes32 public constant PAUSER_ROLE = keccak256("PAUSER_ROLE");\n' : ""}${royalty ? '    bytes32 public constant ROYALTY_MANAGER_ROLE = keccak256("ROYALTY_MANAGER_ROLE");\n' : ""}
    constructor(
        address admin_,
        string memory uri_${royalty ? ",\n        address royaltyReceiver_,\n        uint96 royaltyBps_" : ""}
    ) ERC1155(uri_) {
        require(admin_ != address(0), "admin required");

        _grantRole(DEFAULT_ADMIN_ROLE, admin_);
${mintable ? "        _grantRole(MINTER_ROLE, admin_);\n" : ""}${pausable ? "        _grantRole(PAUSER_ROLE, admin_);\n" : ""}${royalty ? "        _grantRole(ROYALTY_MANAGER_ROLE, admin_);\n\n        if (royaltyReceiver_ != address(0) && royaltyBps_ > 0) {\n            _setDefaultRoyalty(royaltyReceiver_, royaltyBps_);\n        }\n" : ""}
    }

    function setURI(string memory newURI) external onlyRole(DEFAULT_ADMIN_ROLE) {
        _setURI(newURI);
    }
${mintable ? `
    function mint(address to, uint256 id, uint256 amount, bytes memory data)
        external
        onlyRole(MINTER_ROLE)
    {
        _mint(to, id, amount, data);
    }
` : ""}${pausable ? `
    function pause() external onlyRole(PAUSER_ROLE) {
        _pause();
    }

    function unpause() external onlyRole(PAUSER_ROLE) {
        _unpause();
    }

    function _update(
        address from,
        address to,
        uint256[] memory ids,
        uint256[] memory values
    ) internal override(ERC1155, ERC1155Pausable) {
        super._update(from, to, ids, values);
    }
` : ""}${royalty ? `
    function setDefaultRoyalty(address receiver, uint96 feeNumerator)
        external
        onlyRole(ROYALTY_MANAGER_ROLE)
    {
        _setDefaultRoyalty(receiver, feeNumerator);
    }

    function deleteDefaultRoyalty() external onlyRole(ROYALTY_MANAGER_ROLE) {
        _deleteDefaultRoyalty();
    }
` : ""}
    function supportsInterface(bytes4 interfaceId)
        public
        view
        ${supportsOverride}
        returns (bool)
    {
        return super.supportsInterface(interfaceId);
    }
}
`;
}

export function buildCustomBuilderSeed({ search, state }) {
    const seed = getSeedFromSearch(search, state);

    let source;

    if (seed.tokenType === "ERC721") {
        source = buildErc721Source(seed);
    } else if (seed.tokenType === "ERC1155") {
        source = buildErc1155Source(seed);
    } else {
        source = buildErc20Source(seed);
    }

    return {
        ...seed,
        source,
    };
}